/* eslint-disable react/prop-types */
/* eslint-disable no-unused-vars */
import React, { useEffect, useMemo, useState, useRef } from 'react';
import { LixaniFiles } from 'lixani-lib-graphql';
import { Translation } from 'react-i18next';
import Dialog from 'components/DialogWrapper/DialogWrapper';
import DialogContent from '@material-ui/core/DialogContent';
import Grid from '@material-ui/core/Grid';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import Button from '@material-ui/core/Button';
import Collapse from '@material-ui/core/Collapse';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import ExpandLessIcon from '@material-ui/icons/ExpandLess';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import DialogHeader from 'components/DialogHeader/DialogHeader';
import Spinner from 'components/Spinner/Spinner';

import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';

import DeleteDialog from 'scenes/ProjectGallery/components/DeleteDialog';
import DescriptionDialog from 'scenes/ProjectGallery/components/DescriptionDialog';
import { setSelectedFilePermissionId } from 'scenes/ProjectGallery/store/actions/projectGallery.actions';

import { selectTargetInteractionsCommentsAndEmojisWithJoinedUser } from 'store/interactions/selectors/interactions.selectors';
import { createInteraction } from 'store/interactions/thunks/createInteraction';
import CommentsSection from 'components/commentsSection/commentsSection';
import { InfoContent } from './InfoContent';
import { NavigateButtons } from './NavigateButtons';
import { DialogActions } from './DialogActions';
import FullScreenComponent from './FullScreenComponent';
import { getFileDownloadSignedUrl } from 'store/files/services/getFileDownloadSignedUrl.service';
import MobileImageViewer from './MobileImageViewer';
import {
  useMobilePhotoGestures,
  isMobileViewer,
} from './useMobilePhotoGestures';
import { getSignedUrlCached } from 'scenes/ProjectGallery/utils/signedUrlCache';
import { logActivityEnded } from 'store/activityLog/thunks/logActivityEnded.thunk';
import { logImageOpened } from 'store/activityLog/thunks/logImageOpened.thunk';
import WhoViewedImageDialog from './WhoViewedImageDialog';
import { selectIsRequiredPermissionForProject } from 'scenes/Permissions/store/selectors/personalPermissionsSelectors';
import { ACTIVITY_LOG_VIEW } from 'scenes/Permissions/constants/permissions';
import useWindowSize from 'hooks/useWindowSize';
import { loadInteractionsByTarget } from 'store/interactions/thunks/loadInteractionsByTarget';
import { selectIsUpdatePending } from 'scenes/ProjectGallery/store/selectors/project-gallery-base.selectors';
import {
  selectProjectFloorplansArray,
  selectFloorplanPlacementsForFile,
} from 'store/floorplans/selectors/floorplans.selectors';
import { loadProjectFloorplans } from 'store/floorplans/thunks/loadProjectFloorplans.thunk';
import { fetchImageFloorplanPlacements } from 'store/floorplanPlacements';
import { refreshProjectMediaTagsData } from 'store/mediaTags/thunks/refreshProjectMediaTagsData.thunk';
import { FloorplanThumbnail } from 'components/PreviewDialog/components/FloorplanThumbnail';
import {
  IMAGE_PREVIEW_CAPABILITIES_ALL,
  getActionBarCapabilities,
  showsBottomOverlay,
  showsMobileTagRow,
} from './imagePreviewCapabilities';
import { MobileAddMediaTagSheet } from './MobileMediaTags/MobileAddMediaTagSheet';
import { MobileMediaTagRow } from './MobileMediaTags/MobileMediaTagRow';
import { MobileMediaTagSheet } from './MobileMediaTags/MobileMediaTagSheet';
import { VIEWER_OVERLAY_GUARDS } from './MobileMediaTags/mobileMediaTags';

const useStyles = makeStyles((theme) => ({
  dialogPaper: {
    ...theme.component.dialogPaper,
    border: theme.component.border,
  },

  imageGrid: {
    position: 'relative',
    height: '80%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    padding: '0',
    background: 'rgb(0,0,0)',
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      height: 'auto',
      minHeight: 0,
      width: '100%',
    },
  },
  commentsOverlay: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    maxHeight: '55%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    minHeight: 0,
    zIndex: 30,
    pointerEvents: 'none',
    paddingBottom: 'env(safe-area-inset-bottom, 0px)',
  },

  image: {
    width: '100%',
    height: '100%',

    objectFit: 'contain',
  },
  infoTable: {
    ...theme.typography.fontPantonLight,
    marginTop: '10px',
    fontSize: '0.87rem',
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    '& th': {
      ...theme.typography.fontPantonSemiBold,
      fontSize: '0.87rem',
      paddingBottom: 12,
      paddingRight: 4,
      paddingTop: 0,
      textAlign: 'left',
    },
    '& td': {
      paddingBottom: 12,
    },
  },

  scrollContainer: {
    display: 'flex',
    position: 'absolute',
    top: '1%',
    left: '1%',

    overflow: 'auto',
    maxHeight: '80%',
    flexDirection: 'column-reverse',
    width: '100%',

    /* Hide scrollbar for Chrome, Safari and Opera */
    '&::-webkit-scrollbar': {
      display: 'none',
    },
    /* Hide scrollbar for IE, Edge and Firefox */
    scrollbarWidth: 'none',
    msOverflowStyle: 'none',
  },

  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 4,
    fontSize: '2rem',
    [theme.breakpoints.down('xs')]: {},
  },

  gridContainer: {
    height: '100%',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      flexWrap: 'nowrap',
      height: '100%',
      minHeight: 0,
    },
  },
  gridItem: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      minHeight: 0,
      maxWidth: '100%',
      flexBasis: 'auto',
    },
  },
  gridItemImageMobile: {
    [theme.breakpoints.down('sm')]: {
      flex: '1 1 auto',
      height: 'auto',
      minHeight: '60vh',
    },
  },
  gridItemFloorplanMobile: {
    [theme.breakpoints.down('sm')]: {
      flex: '0 0 auto',
      height: 'auto',
      minHeight: 0,
    },
  },
  dialogActionsContainer: {
    maxHeight: '5%',
  },
  commentsSectionContainer: {
    height: '100%',
    padding: '4px',
  },
  dialogContent: {
    margin: '0px',
    padding: '0px',
    [theme.breakpoints.down('sm')]: {
      overflowY: 'auto',
      overflowX: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  actionsCommentsContainer: {
    width: '100%',
    height: '99%',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5%',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      gap: 0,
    },
  },
  onImageButtons: {
    position: 'absolute',
    top: '0',
    width: '100%',
    [theme.breakpoints.down('sm')]: {
      zIndex: 20,
      // A shade over the top of the photo, so the white glyphs of the top
      // bar read on a bright picture too; it fades out by the bar's lower
      // edge.
      backgroundImage:
        'linear-gradient(to bottom, rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0))',
    },
  },
  floorplanThumbnails: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: '4px',
    overflowY: 'auto',
    height: '50%',
    [theme.breakpoints.down('sm')]: {
      height: 'auto',
      overflowY: 'visible',
    },
  },
  floorplanToggle: {
    ...theme.typography.fontPantonSemiBold,
    textTransform: 'none',
    fontSize: '0.87rem',
    padding: '4px 8px',
    alignSelf: 'flex-start',
  },
  mobileVideoCommentsContainer: {
    width: '100%',
    padding: '4px',
    maxHeight: '50vh',
    display: 'flex',
    flexDirection: 'column',
  },
}));

// Which of the phone's tag sheets (MobileMediaTags) the tag row opened, and
// for which tag. Closing keeps the rest, so the sheet slides out as it was.
const CLOSED_TAG_SHEET = { kind: null, tagLinkId: null, open: false };

// The same object when it is closed already, so the update renders nothing.
const closeTagSheet = (sheet) =>
  sheet.open ? { ...sheet, open: false } : sheet;

const isTagSheetOpen = (sheet, kind) => sheet.open && sheet.kind === kind;

function ImagePreviewDialog(props) {
  const ref = useRef(null);
  const stripRef = useRef(null);
  const zoomLayerRef = useRef(null);
  const videoApiRef = useRef(null);

  const dispatch = useDispatch();
  const theme = useTheme();
  const {
    filePermission,
    open,
    onClose,
    handleDeletePicture,
    handleSave,
    mediaFilePermissions,
    filePermissionIdsUserHasPermissionToEdit,
    handleSaveDescription,
    openDescriptionDialog,
    handleOpenDescriptionDialog,
    handleCloseDescriptionDialog,
    // Optional host overrides (the project bank reuses this dialog over its
    // own permissions and update thunk); the gallery hosts pass neither and
    // keep the defaults below.
    updatePending: updatePendingProp,
    hasPermissionToDelete: hasPermissionToDeleteProp,
    // Paging to another image. The gallery callers leave this unset and the
    // dialog selects the target in the gallery store; a caller with its own
    // list (the chat) receives the target id and re-renders with it.
    onNavigate,
    capabilities = IMAGE_PREVIEW_CAPABILITIES_ALL,
  } = props;

  const [src, setSrc] = useState(null);
  const editableIds = filePermissionIdsUserHasPermissionToEdit || [];
  const hasPermissionToEdit =
    capabilities.edit && editableIds.includes(filePermission.id);
  const hasPermissionToDelete =
    hasPermissionToDeleteProp ?? hasPermissionToEdit;

  // Prev / next page over this list; a host that hands in no list (or an
  // empty one) gets a single-item viewer instead of a crash on `[0]`.
  const navigationList =
    mediaFilePermissions && mediaFilePermissions.length
      ? mediaFilePermissions
      : [filePermission];

  const [dimensions, setDimensions] = useState({});
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [floorplansOpen, setFloorplansOpen] = useState(false);
  const [whoViewedOpen, setWhoViewedOpen] = useState(false);
  const [tagSheet, setTagSheet] = useState(CLOSED_TAG_SHEET);

  const commentsAndEmojis = useSelector((state) =>
    selectTargetInteractionsCommentsAndEmojisWithJoinedUser(
      state,
      filePermission.id
    )
  );

  const floorplans = useSelector(selectProjectFloorplansArray);
  const floorplanPlacements = useSelector((state) =>
    selectFloorplanPlacementsForFile(state, filePermission.id)
  );
  const hasFloorplanPlacements =
    capabilities.floorplans && floorplanPlacements.length > 0;

  const filePermissionRotation = filePermission.rotation
    ? filePermission.rotation
    : 0;

  const isVideo =
    filePermission && filePermission.file.mimeType.includes('video');

  const [rotation, setRotation] = useState(filePermissionRotation);
  const [isFullScreen, setFullScreen] = useState(false);

  const { width, height } = useWindowSize();

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
  };

  const onOpenDeleteDialog = () => {
    setOpenDeleteDialog(true);
  };

  const galleryUpdatePending = useSelector(selectIsUpdatePending);
  const updatePending = updatePendingProp ?? galleryUpdatePending;

  // Gate the "who viewed this image" info button on ACTIVITY_LOG_VIEW for the
  // image's own project (which may differ from the currently-selected project).
  const canViewActivityLogs =
    useSelector((state) =>
      selectIsRequiredPermissionForProject(state, {
        projectId: filePermission.project?.id,
        permission: ACTIVITY_LOG_VIEW,
      })
    ) && capabilities.activityLog;

  const classes = useStyles();

  const project = filePermission.project;

  const showSaveButton = () => {
    return filePermissionRotation !== rotation;
  };

  const initialRotate = () => {
    if (!isVideo && ref.current) {
      if (filePermission.rotation) {
        ref.current.style.transform = `rotate(${filePermissionRotation}deg)`;
      }
    }
  };

  async function requestFileEffect() {
    setRotation(filePermissionRotation);
    setDimensions({});
    setZoomLevel(1);
    setImagePosition({ x: 0, y: 0 });

    const imageUrl = await getSignedUrlCached(filePermission.id);
    console.log('presignedUrl: ', imageUrl);

    if (imageUrl) {
      setSrc(imageUrl);
      initialRotate();
    } else {
      setSrc(theme.component.imagePlaceholder);
    }
  }

  const onDownloadImage = async () => {
    const presignedUrl = await getFileDownloadSignedUrl(filePermission.id);

    const blob = await fetch(presignedUrl).then((res) => res.blob());
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    // Set link's href to point to the Blob URL
    link.href = url;
    link.download = filePermission.name;
    // Append link to the body
    document.body.appendChild(link);

    // Dispatch click event on the link
    // This is necessary as link.click() does not work on the latest firefox
    link.dispatchEvent(
      new MouseEvent('click', {
        bubbles: true,
        cancelable: true,
        view: window,
      })
    );

    // Remove link from body
    document.body.removeChild(link);
  };

  useEffect(() => {
    requestFileEffect();
    if (capabilities.interactions) {
      dispatch(loadInteractionsByTarget(filePermission.id));
    }
    if (capabilities.floorplans) {
      dispatch(loadProjectFloorplans(filePermission.project.id));
      dispatch(
        fetchImageFloorplanPlacements({ projectId: filePermission.project.id })
      );
    }
    // The gallery read the tags when it opened; the AI tagger may have added
    // some since.
    if (capabilities.tags) {
      dispatch(refreshProjectMediaTagsData());
    }
  }, [filePermission]); // eslint-disable-line react-hooks/exhaustive-deps

  // Log VIEW_IMAGE once per distinct image (ref-guarded on the file-permission id,
  // so paging next/prev re-logs each image). Mirrors VIEW_RECORD — the file
  // permission is the target; the backend resolves the project's business_id.
  // The dispatch promise is kept on activeViewLogRef so the end-effect below can
  // stamp endedAt even when the view ends before the create response resolves.
  const loggedImageViewIdRef = useRef(null);
  const activeViewLogRef = useRef(null); // { filePermissionId, projectId, promise }
  useEffect(() => {
    if (
      capabilities.activityLog &&
      open &&
      filePermission?.id &&
      filePermission?.project?.id &&
      loggedImageViewIdRef.current !== filePermission.id
    ) {
      loggedImageViewIdRef.current = filePermission.id;
      const promise = dispatch(
        logImageOpened({
          projectId: filePermission.project.id,
          filePermissionId: filePermission.id,
        })
      );
      activeViewLogRef.current = {
        filePermissionId: filePermission.id,
        projectId: filePermission.project.id,
        promise,
      };
    }
  }, [dispatch, open, filePermission]);

  // End the active view log when the user pages to another image (id change) or
  // closes the dialog (both parents unmount it on close). Keyed on the id, not
  // the object, so e.g. a rotation save doesn't end the view. Chains on the
  // create promise so a view that ends before the response resolves is still
  // stamped; endedAt stays absent when the tab is closed instead.
  useEffect(() => {
    const filePermissionId = filePermission?.id;
    return () => {
      const active = activeViewLogRef.current;
      if (!active || active.filePermissionId !== filePermissionId) return;
      activeViewLogRef.current = null;
      active.promise.then((action) => {
        const sk = action?.payload?.activityLog?.sk;
        if (sk) {
          dispatch(logActivityEnded({ projectId: active.projectId, sk }));
        }
      });
    };
  }, [dispatch, filePermission?.id]);

  const isMobile =
    /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|WPDesktop/i.test(
      navigator.userAgent
    );

  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  // `noSsr`, so a phone draws its own layout, its tags in the row at the
  // bottom of the photo rather than in the top bar, from the first render.
  const downSm = useMediaQuery(theme.breakpoints.down('sm'), { noSsr: true });
  // What the action bar offers: no media tags below md (a phone turned
  // sideways included); see getActionBarCapabilities.
  const actionCapabilities = useMemo(
    () => getActionBarCapabilities(capabilities, downSm),
    [downSm, capabilities]
  );

  // Below md the comments and the tag row lie over the bottom of the photo.
  const overlayLayout = {
    isPhoneLayout: downSm,
    isFullScreen,
    isVideo,
    isMobileViewer,
  };
  const overlayAllowed = showsBottomOverlay(overlayLayout);
  const showTagRow = showsMobileTagRow({ capabilities, ...overlayLayout });

  // A tag sheet closes with the photo it was about, and with the row (the
  // viewer grown to md, say).
  useEffect(() => {
    setTagSheet(closeTagSheet);
  }, [filePermission.id, showTagRow]);

  const tagRow = showTagRow ? (
    <MobileMediaTagRow
      onAddTag={() => setTagSheet({ kind: 'add', tagLinkId: null, open: true })}
      onOpenTag={(tagLinkId) =>
        setTagSheet({ kind: 'detail', tagLinkId, open: true })
      }
    />
  ) : null;

  const handleImgLoad = ({ target: img }) => {
    setDimensions({ height: img.naturalHeight, width: img.naturalWidth });
  };

  const changeSelectedMediaFilePermission = (number) => {
    const currentIndex = navigationList.findIndex(
      (fp) => fp.id === filePermission.id
    );
    if (currentIndex < 0) {
      return;
    }
    const target = navigationList[currentIndex + number];
    if (!target) {
      return;
    }
    if (!isMobileViewer) {
      // Desktop renders from `src` directly — clearing it keeps the old
      // image from flashing with the new image's rotation below. The mobile
      // pager renders its own prefetched panes and must NOT blank them.
      setSrc(null);
    }
    // Rotation must follow the id in the same commit, or the pager's new
    // center pane renders one frame with the previous image's rotation.
    setRotation(target.rotation || 0);
    if (onNavigate) {
      onNavigate(target.id);
    } else {
      dispatch(setSelectedFilePermissionId(target.id));
    }
  };

  const handleReactToFilepermission = (emoji) => {
    dispatch(
      createInteraction({
        projectId: filePermission.project.id,
        targetId: filePermission.id,
        content: emoji,
        contentType: 'EMOJI',
        targetType: 'FILEPERMISSION',
      })
    );
  };

  const handleCreateComment = (comment) => {
    dispatch(
      createInteraction({
        projectId: filePermission.project.id,
        targetId: filePermission.id,
        content: comment,
        contentType: 'COMMENT',
        targetType: 'FILEPERMISSION',
      })
    );
  };

  const handleRotate = async (angle) => {
    console.log(ref.current);
    let newRotation = rotation + angle;
    if (newRotation === 360) newRotation = 0;
    if (newRotation === -90) newRotation = 270;
    if (isMobileViewer) {
      // The pan/zoom bounds change with the orientation — start from 1x.
      resetGestures();
    }
    if (ref.current) {
      ref.current.style.transform = `rotate(${newRotation}deg)`;
    }
    setRotation(newRotation);
  };
  const handleSaveRotation = (angle) => {
    const updateParams = { rotation: angle };
    handleSave(updateParams);
  };

  // An image that is no longer in the list (a tag filter is on and the tag
  // was just removed from it) has no neighbours, so both chevrons hide.
  const currentMediaIndex = navigationList.findIndex(
    (fp) => fp.id === filePermission.id
  );
  const isFirstFilePermission = currentMediaIndex <= 0;
  const isLastFilePermission =
    currentMediaIndex < 0 || currentMediaIndex === navigationList.length - 1;

  // The desktop side column holds the comments and the floorplan thumbnails.
  // With neither to show, the image takes the whole width.
  const showSidePanel = capabilities.interactions || hasFloorplanPlacements;

  const toggleFullScreen = () => {
    setFullScreen((prev) => !prev);
  };

  // Swiping can land on a video while in fullscreen mode; DialogActions
  // hides its exit-fullscreen toggle for videos, which would trap the user
  // behind the fixed overlay. Videos never render fullscreen — drop out.
  useEffect(() => {
    if (isVideo && isFullScreen) {
      setFullScreen(false);
    }
  }, [isVideo, isFullScreen]);

  const prevFilePermission =
    currentMediaIndex > 0 ? navigationList[currentMediaIndex - 1] : null;
  const nextFilePermission =
    currentMediaIndex >= 0 && currentMediaIndex < navigationList.length - 1
      ? navigationList[currentMediaIndex + 1]
      : null;

  // iPhone-Photos-style gestures on mobile (swipe to page, pinch, double-tap,
  // clamped pan). Only wired to the DOM when isMobileViewer — desktop keeps
  // the mouse/wheel handlers below untouched.
  const {
    handlers: gestureHandlers,
    isZoomed,
    reset: resetGestures,
  } = useMobilePhotoGestures({
    stripRef,
    zoomLayerRef,
    imgRef: ref,
    rotation,
    isVideo,
    canGoPrev: Boolean(prevFilePermission),
    canGoNext: Boolean(nextFilePermission),
    onNavigate: changeSelectedMediaFilePermission,
    onVideoTap: () => {
      if (videoApiRef.current) videoApiRef.current.togglePlay();
    },
    currentId: filePermission.id,
  });

  // THE WHOLE ZOOMING FEATURE IS VERY VIBE CODED...
  const [zoomLevel, setZoomLevel] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });

  const [initialDistance, setInitialDistance] = useState(null);
  const [startZoom, setStartZoom] = useState(1);

  const handleWheel = (e) => {
    if (isVideo || isMobile) {
      return;
    }
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    const newZoom = Math.max(0.5, Math.min(5, zoomLevel + delta));
    setZoomLevel(newZoom);

    // Reset position to center when zooming out to 1x
    if (newZoom === 1) {
      setImagePosition({ x: 0, y: 0 });
    }
  };

  const handleMouseDown = (e) => {
    if (isVideo) {
      return;
    }
    if (e.touches?.length > 1) {
      e.preventDefault(); // prevents pinch zoom on iOS
    }
    if (zoomLevel !== 1) {
      setIsDragging(true);
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      setLastMousePosition({ x: clientX, y: clientY });
    }
  };

  function getDistance(touches) {
    const [a, b] = touches;
    const dx = a.clientX - b.clientX;
    const dy = a.clientY - b.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }

  const handleMouseMove = (e) => {
    if (isVideo) {
      return;
    }
    if (e.touches?.length > 1) {
      e.preventDefault(); // prevents pinch zoom on iOS
    }

    if (isDragging && zoomLevel !== 1) {
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      const deltaX = clientX - lastMousePosition.x;
      const deltaY = clientY - lastMousePosition.y;
      setImagePosition((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));
      setLastMousePosition({ x: clientX, y: clientY });
    }
  };

  const handleMouseUp = (e) => {
    if (isVideo || isMobile) {
      return;
    }
    if (e.detail > 1) {
      e.preventDefault(); // blocks double-tap zoom
    }
    setIsDragging(false);
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dist = getDistance(e.touches);
      setInitialDistance(dist);
      setStartZoom(zoomLevel); // store zoom at pinch begin
    } else if (e.touches.length === 1) {
      const touch = e.touches[0];
      setLastMousePosition({ x: touch.clientX, y: touch.clientY });
      setIsDragging(true);
    }
  };

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 3;

  const handleTouchMove = (e) => {
    if (isVideo) return;

    if (e.touches.length > 1) {
      e.preventDefault();
    }

    // --- PINCH ZOOM ---
    if (e.touches.length === 2) {
      const newDist = getDistance(e.touches);
      if (!initialDistance) return;

      const scaleFactor = newDist / initialDistance;

      // absolute zoom based on startZoom + current pinch
      let newZoom = startZoom * scaleFactor;

      // clamp zoom
      newZoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, newZoom));

      setZoomLevel(newZoom);
      return; // stop dragging
    }

    // --- DRAG ---
    if (e.touches.length === 1 && isDragging) {
      const touch = e.touches[0];
      const deltaX = touch.clientX - lastMousePosition.x;
      const deltaY = touch.clientY - lastMousePosition.y;

      setImagePosition((prev) => ({
        x: prev.x + deltaX,
        y: prev.y + deltaY,
      }));

      setLastMousePosition({ x: touch.clientX, y: touch.clientY });
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      setInitialDistance(null);
    }
    setIsDragging(false);
  };

  const handleClick = (e) => {
    if (isVideo || isMobile) {
      return;
    }
    if (e.detail > 1) {
      e.preventDefault();
    }
    if (zoomLevel === 1) {
      setZoomLevel(2);
    }
  };

  const handleDoubleClick = (e) => {
    if (isVideo || isMobile) {
      return;
    }
    if (e.detail > 1) {
      e.preventDefault(); // blocks double-tap zoom
    }
    if (zoomLevel === 1) {
      setZoomLevel(2);
    } else {
      setZoomLevel(1);
      setImagePosition({ x: 0, y: 0 });
    }
  };

  return (
    <Translation ns={['common']}>
      {(t) => (
        <>
          <Dialog
            aria-labelledby="image-preview-dialog"
            classes={{ paper: classes.dialogPaper }}
            fullScreen={!upSm || isMobile}
            PaperProps={{
              style: {
                width: width,
                height: height,
                maxWidth: 'none',
              },
            }}
            open={open}
            onClose={onClose}
          >
            <DialogHeader title={filePermission.name} onClose={props.onClose} />

            <DialogContent className={classes.dialogContent}>
              <Grid container spacing={0} className={classes.gridContainer}>
                <Grid
                  item
                  className={`${classes.gridItem} ${classes.gridItemImageMobile}`}
                  md={showSidePanel ? 9 : 12}
                  xs={12}
                >
                  {capabilities.info && !downSm ? (
                    <InfoContent
                      filePermission={filePermission}
                      project={project}
                      dimensions={dimensions}
                    />
                  ) : null}
                  <div
                    className={classes.imageGrid}
                    onWheel={handleWheel}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    onDoubleClick={handleDoubleClick}
                    onClick={handleClick}
                    onTouchStart={
                      isMobileViewer
                        ? gestureHandlers.onTouchStart
                        : handleTouchStart
                    }
                    onTouchMove={
                      isMobileViewer
                        ? gestureHandlers.onTouchMove
                        : handleTouchMove
                    }
                    onTouchEnd={
                      isMobileViewer
                        ? gestureHandlers.onTouchEnd
                        : handleTouchEnd
                    }
                    onTouchCancel={
                      isMobileViewer ? gestureHandlers.onTouchCancel : undefined
                    }
                    style={{
                      cursor: isVideo
                        ? 'default'
                        : zoomLevel !== 1
                          ? isDragging
                            ? 'grabbing'
                            : 'grab'
                          : 'zoom-in',
                      userSelect: 'none',
                      WebkitUserSelect: 'none',
                      MozUserSelect: 'none',
                      msUserSelect: 'none',
                      // Mobile videos behave exactly like images (comments
                      // are overlaid, nothing scrolls below) — only the
                      // desktop native-controls video keeps 'auto'.
                      touchAction: isVideo && !isMobileViewer ? 'auto' : 'none',
                    }}
                  >
                    {isMobileViewer ? (
                      <FullScreenComponent
                        isFullScreen={isFullScreen}
                        toggleFullScreen={toggleFullScreen}
                      >
                        <MobileImageViewer
                          filePermission={filePermission}
                          prevFilePermission={prevFilePermission}
                          nextFilePermission={nextFilePermission}
                          rotation={rotation}
                          isZoomed={isZoomed}
                          stripRef={stripRef}
                          zoomLayerRef={zoomLayerRef}
                          imgRef={ref}
                          onImgLoad={handleImgLoad}
                          videoApiRef={videoApiRef}
                        />
                        {isFullScreen ? (
                          <div className={classes.onImageButtons}>
                            <DialogActions
                              handleOpenDescriptionDialog={
                                handleOpenDescriptionDialog
                              }
                              handleRotate={handleRotate}
                              onDownloadImage={onDownloadImage}
                              handleReactToFilepermission={
                                handleReactToFilepermission
                              }
                              onOpenDeleteDialog={onOpenDeleteDialog}
                              handleSaveRotation={handleSaveRotation}
                              src={src}
                              showSaveButton={showSaveButton}
                              updatePending={updatePending}
                              rotation={rotation}
                              isVideo={isVideo}
                              hasPermissionToEdit={hasPermissionToEdit}
                              hasPermissionToDelete={hasPermissionToDelete}
                              toggleFullScreen={toggleFullScreen}
                              isFullScreen={isFullScreen}
                              filePermissionId={filePermission.id}
                              projectId={project?.id}
                              canViewActivityLogs={canViewActivityLogs}
                              capabilities={actionCapabilities}
                              onOpenViewedBy={() => setWhoViewedOpen(true)}
                            ></DialogActions>
                          </div>
                        ) : null}
                      </FullScreenComponent>
                    ) : src &&
                      filePermission?.file?.mimeType?.includes('image') ? (
                      <FullScreenComponent
                        isFullScreen={isFullScreen}
                        toggleFullScreen={toggleFullScreen}
                      >
                        <img
                          className={classes.image}
                          ref={ref}
                          alt={filePermission.name}
                          src={src}
                          style={{
                            transform: `rotate(${rotation}deg) scale(${zoomLevel}) translate(${
                              imagePosition.x / zoomLevel
                            }px, ${imagePosition.y / zoomLevel}px)`,
                            transformOrigin: 'center',
                            transition: isDragging
                              ? 'none'
                              : 'transform 0.1s ease-out',
                            userSelect: 'none',
                            WebkitUserSelect: 'none',
                            MozUserSelect: 'none',
                            msUserSelect: 'none',
                            pointerEvents: 'none',
                          }}
                          onLoad={handleImgLoad}
                          draggable={false}
                        />
                        {isFullScreen ? (
                          <div className={classes.onImageButtons}>
                            <DialogActions
                              handleOpenDescriptionDialog={
                                handleOpenDescriptionDialog
                              }
                              handleRotate={handleRotate}
                              onDownloadImage={onDownloadImage}
                              handleReactToFilepermission={
                                handleReactToFilepermission
                              }
                              onOpenDeleteDialog={onOpenDeleteDialog}
                              handleSaveRotation={handleSaveRotation}
                              src={src}
                              showSaveButton={showSaveButton}
                              updatePending={updatePending}
                              rotation={rotation}
                              isVideo={isVideo}
                              hasPermissionToEdit={hasPermissionToEdit}
                              hasPermissionToDelete={hasPermissionToDelete}
                              toggleFullScreen={toggleFullScreen}
                              isFullScreen={isFullScreen}
                              filePermissionId={filePermission.id}
                              projectId={project?.id}
                              canViewActivityLogs={canViewActivityLogs}
                              capabilities={actionCapabilities}
                              onOpenViewedBy={() => setWhoViewedOpen(true)}
                            ></DialogActions>
                          </div>
                        ) : null}
                      </FullScreenComponent>
                    ) : src &&
                      filePermission.file.mimeType.includes('video') ? (
                      <video className={classes.image} key={src} controls>
                        <source src={src} type={filePermission.file.mimeType} />
                      </video>
                    ) : (
                      <Spinner />
                    )}
                    {!isFullScreen ? (
                      <div className={classes.onImageButtons}>
                        <DialogActions
                          handleOpenDescriptionDialog={
                            handleOpenDescriptionDialog
                          }
                          handleRotate={handleRotate}
                          onDownloadImage={onDownloadImage}
                          handleReactToFilepermission={
                            handleReactToFilepermission
                          }
                          onOpenDeleteDialog={onOpenDeleteDialog}
                          handleSaveRotation={handleSaveRotation}
                          src={src}
                          showSaveButton={showSaveButton}
                          updatePending={updatePending}
                          rotation={rotation}
                          isVideo={isVideo}
                          hasPermissionToEdit={hasPermissionToEdit}
                          hasPermissionToDelete={hasPermissionToDelete}
                          toggleFullScreen={toggleFullScreen}
                          filePermissionId={filePermission.id}
                          projectId={project?.id}
                          canViewActivityLogs={canViewActivityLogs}
                          capabilities={actionCapabilities}
                          onOpenViewedBy={() => setWhoViewedOpen(true)}
                        />
                      </div>
                    ) : null}
                    {capabilities.info && downSm && !isFullScreen ? (
                      <InfoContent
                        filePermission={filePermission}
                        project={project}
                        dimensions={dimensions}
                        downSm={downSm}
                      />
                    ) : null}
                    {downSm && !isFullScreen ? (
                      <NavigateButtons
                        overlay
                        isFirstFilePermission={isFirstFilePermission}
                        isLastFilePermission={isLastFilePermission}
                        changeSelectedMediaFilePermission={
                          changeSelectedMediaFilePermission
                        }
                      />
                    ) : null}
                    {/* Videos join the overlay only on mobile — a narrow
                        desktop window still shows native video controls,
                        which the bottom-anchored overlay would cover. The
                        tag row sits above the comment input, or alone at
                        the bottom without the comments. */}
                    {overlayAllowed &&
                    (capabilities.interactions || showTagRow) ? (
                      <div
                        className={classes.commentsOverlay}
                        {...VIEWER_OVERLAY_GUARDS}
                      >
                        {capabilities.interactions ? (
                          <CommentsSection
                            comments={commentsAndEmojis}
                            handleCreateComment={handleCreateComment}
                            overlay
                            overlayAccessory={tagRow}
                          />
                        ) : (
                          tagRow
                        )}
                      </div>
                    ) : null}
                  </div>
                  {!downSm ? (
                    <NavigateButtons
                      isFirstFilePermission={isFirstFilePermission}
                      isLastFilePermission={isLastFilePermission}
                      changeSelectedMediaFilePermission={
                        changeSelectedMediaFilePermission
                      }
                    />
                  ) : null}
                </Grid>
                {!downSm && showSidePanel ? (
                  <Grid className={classes.gridItem} item md={3} xs={12}>
                    <div className={classes.actionsCommentsContainer}>
                      {capabilities.interactions && (
                        <div
                          className={classes.commentsSectionContainer}
                          style={
                            hasFloorplanPlacements ? { height: '50%' } : {}
                          }
                        >
                          <CommentsSection
                            comments={commentsAndEmojis}
                            handleCreateComment={handleCreateComment}
                          />
                        </div>
                      )}
                      {hasFloorplanPlacements && (
                        <div className={classes.floorplanThumbnails}>
                          {floorplanPlacements.map((placement) => {
                            const floorplan = floorplans.find(
                              (f) => f.id === placement.floorplanId
                            );
                            return (
                              <FloorplanThumbnail
                                key={placement.id}
                                placement={placement}
                                floorplan={floorplan}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </Grid>
                ) : hasFloorplanPlacements ? (
                  <Grid
                    className={`${classes.gridItem} ${classes.gridItemFloorplanMobile}`}
                    item
                    xs={12}
                  >
                    <Button
                      className={classes.floorplanToggle}
                      onClick={() => setFloorplansOpen((prev) => !prev)}
                      startIcon={
                        floorplansOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />
                      }
                    >
                      {t('gallery.view_floorplans')}
                    </Button>
                    <Collapse in={floorplansOpen}>
                      <div className={classes.floorplanThumbnails}>
                        {floorplanPlacements.map((placement) => {
                          const floorplan = floorplans.find(
                            (f) => f.id === placement.floorplanId
                          );
                          return (
                            <FloorplanThumbnail
                              key={placement.id}
                              placement={placement}
                              floorplan={floorplan}
                            />
                          );
                        })}
                      </div>
                    </Collapse>
                  </Grid>
                ) : null}
                {/* Narrow desktop windows keep the native-controls video, so
                    their comments stay below it — mobile videos use the
                    same bottom overlay as images instead. */}
                {capabilities.interactions &&
                downSm &&
                isVideo &&
                !isMobileViewer ? (
                  <Grid
                    className={`${classes.gridItem} ${classes.gridItemFloorplanMobile}`}
                    item
                    xs={12}
                  >
                    <div className={classes.mobileVideoCommentsContainer}>
                      <CommentsSection
                        comments={commentsAndEmojis}
                        handleCreateComment={handleCreateComment}
                      />
                    </div>
                  </Grid>
                ) : null}
              </Grid>
            </DialogContent>

            {openDeleteDialog && (
              <DeleteDialog
                picture={filePermission}
                project={project}
                open={openDeleteDialog}
                onClose={handleCloseDeleteDialog}
                onDelete={handleDeletePicture}
              />
            )}
            {openDescriptionDialog && (
              <DescriptionDialog
                picture={filePermission}
                project={project}
                open={openDescriptionDialog}
                onClose={handleCloseDescriptionDialog}
                onSave={handleSaveDescription}
                loading={updatePending}
              />
            )}
            {whoViewedOpen && (
              <WhoViewedImageDialog
                open={whoViewedOpen}
                onClose={() => setWhoViewedOpen(false)}
                projectId={filePermission.project.id}
                filePermissionId={filePermission.id}
              />
            )}
            {/* The tag row's sheets, outside .imageGrid: React events from
                their portals would reach its gesture handlers otherwise. */}
            {capabilities.tags ? (
              <>
                <MobileAddMediaTagSheet
                  open={isTagSheetOpen(tagSheet, 'add')}
                  onClose={() => setTagSheet(closeTagSheet)}
                />
                <MobileMediaTagSheet
                  open={isTagSheetOpen(tagSheet, 'detail')}
                  tagLinkId={tagSheet.tagLinkId}
                  onClose={() => setTagSheet(closeTagSheet)}
                />
              </>
            ) : null}
          </Dialog>
        </>
      )}
    </Translation>
  );
}

export default LixaniFiles(ImagePreviewDialog);
